/* eslint-disable react/no-multi-comp */
import React, { useEffect, useState } from 'react';

const liveWidgetBaseUrl = 'https://widget.homekeepr.com';

const widgetBaseUrl =
	process.env.REACT_APP_HOMEKEEPR_WIDGET_BASE_URL ?? liveWidgetBaseUrl;
const localWidgetDevEntryPath = 'src/entries/cdn-entry.ts';
const localWidgetCacheBuster = String(Date.now());
const widgetLoadTimeoutInMs = 20000;
const widgetScriptRequests = new Map();
const widgetLoadRetryDelayInMs = 250;

/**
 * @typedef {{
 * 	elementName: string,
 * 	key: string,
 * 	path: string,
 * }} FinanceWidget
 */

export const HOMEKEEPR_FINANCE_WIDGET_KEYS = Object.freeze({
	buyNewHome: 'buy-new-home',
	cashOutRefinance: 'cash-out-refinance',
	howMuchPaid: 'how-much-paid',
	investInHomeImprovements: 'invest-in-home-improvements',
	payExtra: 'pay-extra',
	payLumpSum: 'pay-lump-sum',
	refinance: 'refinance',
	sellAndKeepProceeds: 'sell-and-keep-proceeds',
	yourEquity: 'your-equity',
});

const widgetList = [
	{
		elementName: 'hk-pay-lump-sum-widget',
		key: HOMEKEEPR_FINANCE_WIDGET_KEYS.payLumpSum,
		path: 'pay-lump-sum/widget.js',
	},
	{
		elementName: 'hk-pay-extra-widget',
		key: HOMEKEEPR_FINANCE_WIDGET_KEYS.payExtra,
		path: 'pay-extra/widget.js',
	},
	{
		elementName: 'hk-how-much-paid-widget',
		key: HOMEKEEPR_FINANCE_WIDGET_KEYS.howMuchPaid,
		path: 'how-much-paid/widget.js',
	},
	{
		elementName: 'hk-buy-new-home-widget',
		key: HOMEKEEPR_FINANCE_WIDGET_KEYS.buyNewHome,
		path: 'buy-new-home/widget.js',
	},
	{
		elementName: 'hk-cash-out-refinance-widget',
		key: HOMEKEEPR_FINANCE_WIDGET_KEYS.cashOutRefinance,
		path: 'cash-out-refinance/widget.js',
	},
	{
		elementName: 'hk-invest-in-home-improvements-widget',
		key: HOMEKEEPR_FINANCE_WIDGET_KEYS.investInHomeImprovements,
		path: 'invest-in-home-improvements/widget.js',
	},
	{
		elementName: 'hk-refinance-widget',
		key: HOMEKEEPR_FINANCE_WIDGET_KEYS.refinance,
		path: 'refinance/widget.js',
	},
	{
		elementName: 'hk-sell-and-keep-proceeds-widget',
		key: HOMEKEEPR_FINANCE_WIDGET_KEYS.sellAndKeepProceeds,
		path: 'sell-and-keep-proceeds/widget.js',
	},
	{
		elementName: 'hk-your-equity-widget',
		key: HOMEKEEPR_FINANCE_WIDGET_KEYS.yourEquity,
		path: 'your-equity/widget.js',
	},
];

/**
 * @param {FinanceWidget[]} widgets
 * @param {string[] | undefined} widgetKeys
 * @param {string[] | undefined} hiddenWidgetKeys
 * @returns {FinanceWidget[]}
 */
function filterWidgets(widgets, widgetKeys, hiddenWidgetKeys) {
	const allowedWidgetKeySet = widgetKeys?.length ? new Set(widgetKeys) : null;
	const hiddenWidgetKeySet = new Set(hiddenWidgetKeys ?? []);

	return widgets.filter((widget) => {
		if (allowedWidgetKeySet && !allowedWidgetKeySet.has(widget.key)) {
			return false;
		}

		return !hiddenWidgetKeySet.has(widget.key);
	});
}

/**
 * @param {string} path
 * @returns {string}
 */
function getWidgetScriptUrl(path) {
	const baseUrl = widgetBaseUrl.replace(/\/$/, '');

	if (!isLocalWidgetBaseUrl()) {
		return `${baseUrl}/${path}`;
	}

	const url = `${baseUrl}/${localWidgetDevEntryPath}`;

	return `${url}?v=${encodeURIComponent(localWidgetCacheBuster)}`;
}

/**
 * @returns {boolean}
 */
function isLocalWidgetBaseUrl() {
	const baseUrl = widgetBaseUrl.replace(/\/$/, '');

	return baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
}

/**
 * @param {string} url
 * @returns {string}
 */
function getWidgetScriptId(url) {
	return `homekeepr-finance-widget-${btoa(url).replace(/[^a-zA-Z0-9]/g, '')}`;
}

/**
 * @param {string} url
 * @returns {Promise<void>}
 */
function loadWidgetScript(url) {
	const scriptId = getWidgetScriptId(url);

	if (widgetScriptRequests.has(scriptId)) {
		return widgetScriptRequests.get(scriptId);
	}

	const existingScript = document.getElementById(scriptId);

	if (existingScript?.dataset.isLoaded === 'true') {
		const request = Promise.resolve();
		widgetScriptRequests.set(scriptId, request);

		return request;
	}

	if (existingScript?.dataset.isLoaded === 'false') {
		existingScript.remove();
	}

	const request = new Promise(
		/**
		 * @param {(value?: void) => void} resolve
		 * @param {(error: Error) => void} reject
		 * @returns {void}
		 */
		(resolve, reject) => {
			const script =
				existingScript?.dataset.isLoaded === 'false'
					? document.createElement('script')
					: /** @type {HTMLScriptElement} */ (
							existingScript ?? document.createElement('script')
					  );

			script.addEventListener('load', () => {
				script.dataset.isLoaded = 'true';
				resolve();
			});

			script.addEventListener('error', () => {
				script.dataset.isLoaded = 'false';
				widgetScriptRequests.delete(scriptId);
				reject(
					new Error(`Unable to load HomeKeepr widget script: ${url}`)
				);
			});

			if (!script.parentElement) {
				script.async = true;
				script.defer = true;
				script.id = scriptId;
				script.src = url;
				script.type = isLocalWidgetBaseUrl()
					? 'module'
					: 'text/javascript';

				document.head.appendChild(script);
			}
		}
	);

	widgetScriptRequests.set(scriptId, request);

	return request;
}

/**
 * @param {string} url
 * @returns {void}
 */
function clearWidgetScriptRequest(url) {
	const scriptId = getWidgetScriptId(url);
	const existingScript = document.getElementById(scriptId);

	widgetScriptRequests.delete(scriptId);
	existingScript?.remove();
}

/**
 * @param {string} elementName
 * @returns {Promise<void>}
 */
function waitForWidgetElement(elementName) {
	if (customElements.get(elementName)) {
		return Promise.resolve();
	}

	return Promise.race([
		customElements.whenDefined(elementName),
		new Promise((resolve, reject) => {
			void resolve;
			window.setTimeout(() => {
				reject(
					new Error(
						`HomeKeepr widget element was not registered: ${elementName}`
					)
				);
			}, widgetLoadTimeoutInMs);
		}),
	]).then(() => undefined);
}

/**
 * @returns {JSX.Element}
 */
function FinanceWidgetLoader() {
	return (
		<div
			aria-label="Loading finance widget"
			className="homekeepr-finance-widget-loader"
			role="status"
		>
			<div className="homekeepr-finance-widget-loader-header">
				<span />
				<span />
			</div>
			<div className="homekeepr-finance-widget-loader-result" />
			<div className="homekeepr-finance-widget-loader-body">
				<span />
				<span />
				<span />
			</div>
		</div>
	);
}

/**
 * @param {{attributes: {[key: string]: string}, reloadKey?: string, widget: FinanceWidget}} props
 * @returns {JSX.Element | null}
 */
function HomeKeeprFinanceWidget(props) {
	const scriptUrl = getWidgetScriptUrl(props.widget.path);
	const [status, setStatus] = useState(
		customElements.get(props.widget.elementName) ? 'ready' : 'loading'
	);

	useEffect(() => {
		let isMounted = true;
		let retryTimeoutId;

		/**
		 * @param {number} retryCount
		 * @returns {Promise<void>}
		 */
		async function loadWidget(retryCount = 0) {
			try {
				await loadWidgetScript(scriptUrl);
				await waitForWidgetElement(props.widget.elementName);

				if (isMounted) {
					setStatus('ready');
				}
			} catch (_error) {
				if (!isMounted) {
					return;
				}

				if (retryCount < 1) {
					clearWidgetScriptRequest(scriptUrl);
					retryTimeoutId = window.setTimeout(() => {
						void loadWidget(retryCount + 1);
					}, widgetLoadRetryDelayInMs);

					return;
				}

				setStatus('error');
			}
		}

		setStatus(
			customElements.get(props.widget.elementName) ? 'ready' : 'loading'
		);

		void loadWidget();

		return () => {
			isMounted = false;
			window.clearTimeout(retryTimeoutId);
		};
	}, [props.widget.elementName, scriptUrl]);

	if (status === 'error') {
		return (
			<div className="homekeepr-finance-widget-error" role="status">
				{'Finance widget unavailable.'}
			</div>
		);
	}

	if (status !== 'ready') {
		return <FinanceWidgetLoader />;
	}

	return React.createElement(props.widget.elementName, {
		...props.attributes,
		key: `${props.widget.key}-${props.reloadKey ?? ''}`,
		style: {
			display: 'block',
			width: '100%',
		},
	});
}

/**
 * @param {{
 * 	attributes: {[key: string]: string},
 * 	className?: string,
 * 	hiddenWidgetKeys?: string[],
 * 	reloadKey?: string,
 * 	title?: string,
 * 	widgetAttributes?: {[key: string]: {[key: string]: string}},
 * 	widgetKeys?: string[],
 * }} props
 * @returns {JSX.Element}
 */
export default function FinaceWidgets(props) {
	const visibleWidgetList = filterWidgets(
		widgetList,
		props.widgetKeys,
		props.hiddenWidgetKeys
	);

	return (
		<section className={props.className ?? 'mt-10'}>
			{props.title ? (
				<h2 className="h-ms-4 mb-6">{props.title}</h2>
			) : null}
			<div className="homekeepr-finance-widget-grid grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
				{visibleWidgetList.map((widget) => (
					<section
						className="min-w-0"
						data-widget-key={widget.key}
						key={`${widget.key}-${props.reloadKey ?? ''}`}
					>
						<HomeKeeprFinanceWidget
							attributes={{
								...props.attributes,
								...(props.widgetAttributes?.[widget.key] ?? {}),
							}}
							reloadKey={props.reloadKey}
							widget={widget}
						/>
					</section>
				))}
			</div>
		</section>
	);
}
